import React, { useEffect, useState } from "react";
import {
  getRoles,
  getUserRoles,
  updateUser,
  postUser,
} from "../apis/accountRequests";
import { useNavigate } from "react-router-dom";
import { makeRequest } from "../apis/makeRequest";
import { useDispatch, useSelector } from "react-redux";
import { notificationActions } from "../actions/notificationActions";
import { userSchema } from "../validation/userSchema";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Grid,
  Button,
  List,
  ListItemText,
  Checkbox,
  Typography,
  Divider,
  ListItemButton,
  FormControlLabel,
  Box,
} from "@mui/material";
import { FormTextField } from "../controls";
import { getCustomer } from "../apis/backendRequests";

const fields = userSchema.describe().fields;
const passwordErrorCodes = [
  "PasswordTooShort",
  "PasswordRequiresNonAlphanumeric",
  "PasswordRequiresLower",
  "PasswordRequiresUpper",
];

const styles = {
  button: {
    color: "black",
    backgroundColor: "#B5CD7F",
    '&:hover': {
      backgroundColor: "#8BB85A",
    },
    "& .Mui-disabled": {
      backgroundColor: "grey",
    },
    width: "100%",
    boxShadow: "1px 1px 1px 1px gray"
  },
}

export default function UserDetails(props) {
  const { row, setter, setters } = props;
  const account = useSelector((state) => state.account);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(userSchema),
    reValidateMode: "onBlur",
  });
  const [roles, setRoles] = useState([]);
  const [customerData, setCustomerData] = useState([]);
  const [checked, setChecked] = useState([]);
  const [disabledPassword, setDisabledPassword] = useState(false)
  const [active, setActive] = useState(row.isActive)

  useEffect(() => {
    if (!row.dynamicsId || row.roles.includes("Administrador") || row.roles.includes("Admin Ventas")) return;

    makeRequest(
      () => getCustomer(row.dynamicsId),
      (res) => setCustomerData(res.data),
      (err) => dispatch(notificationActions.showError({ message: "Ha ocurrido un error al obtener los datos del cliente" })),
      { checkEmptyResults: false }
    );
  }, [row, account]);

  useEffect(() => {
    if (roles.length !== 0) return;

    makeRequest(
      () => getRoles(),
      (res) => setRoles(res.data),
      (err) => dispatch(notificationActions.showError({ message: "Ha ocurrido un error al obtener los roles" })),
      { checkEmptyResults: false }
    );
  }, [roles]);

  useEffect(() => {
    if (checked.length !== 0) return;

    if (row.id !== undefined) {
      makeRequest(
        () => getUserRoles(row.userName),
        (res) => {
          setChecked(res.data);
        },
        (err) => dispatch(notificationActions.showError({ message: `Ha ocurrido un error al obtener los roles del usuario ${row.userName}` })),
        { checkEmptyResults: false }
      );
    } else {
      return;
    }
  }, [checked, row]);

  const handleToggle = (item) => () => {
    if (item === "Vendedor" && checked.includes("Cliente")) {
      return;
    }

    let newChecked;
    if (
      item === "Administrador" ||
      item === "Vendedor" ||
      item === "Usuario" ||
      item === "Proveedor" ||
      item === "Admin Ventas"
    ) {
      if (checked.includes(item)) {
        newChecked = checked.filter((role) => role !== item);
      } else {
        newChecked = [...checked, item];
      }
    } else {
      newChecked = checked.includes(item)
        ? checked.filter((role) => role !== item)
        : [...checked, item];
    }

    setChecked(newChecked);
  };

  const onSubmit = (data) => {
    const newChecked = [...checked];
    data.isActive = active

    if ((Object.keys(newChecked).length === 0 || (Object.keys(newChecked).length === 1 && newChecked.includes("Usuario")))) {
      dispatch(notificationActions.showError({ message: "Debes asignar un rol más para seguir" }));
      return;
    }


    if (disabledPassword) {
      data.password = null
      data.passwordConfirm = null
    }

    if (checked.find((element) => element === "Usuario") === undefined) {
      newChecked.push("Usuario");
    }
    data.roles = newChecked;
    data.id = row.id;
    if (!data.id) {
      makeRequest(
        () => postUser(data),
        (res) => {
          dispatch(
            notificationActions.showSuccess({
              message: `Se ha creado con éxito el usuario ${data.userName}`,
            })
          );
          navigate("/menu");
          setter(false);
          setters([]);
        },
        (err) => {
          if (!disabledPassword) {
            err.response.data.errors.some((r) =>
              passwordErrorCodes.includes(r.code))
              ?
              dispatch(
                notificationActions.showError({
                  message: "la contraseña no cumple con los requisitos",
                })
              )
              :
              err.response.data.errors.map((i) => {
                if (i.code === 'DuplicateUserName') {
                  setError("userName", { type: "focus", message: "Nombre de usuario ya en uso" }, { shouldFocus: true })
                  dispatch(
                    notificationActions.showError({
                      message: `El nombre de usuario ${data.userName} ya está en uso, por favor ingrese otro`,
                    })
                  )
                } else {
                  dispatch(
                    notificationActions.showError({
                      message: "Ha ocurrido un error al crear el usuario",
                    })
                  );
                }
              }
              )
          } else {
            err.response.data.errors.map((i) => {
              if (i.code === 'DuplicateUserName') {
                setError("userName", { type: "focus", message: "Nombre de usuario ya en uso" }, { shouldFocus: true })
                dispatch(
                  notificationActions.showError({
                    message: `El nombre de usuario ${data.userName} ya está en uso, por favor ingrese otro`,
                  })
                )
              } else {
                dispatch(
                  notificationActions.showError({
                    message: "Ha ocurrido un error al crear el usuario",
                  })
                );
              }
            })
          }
        })
    } else {
      makeRequest(
        () => updateUser(data),
        (res) => {
          dispatch(
            notificationActions.showSuccess({
              message: `Se ha editado con éxito el usuario ${data.userName}`,
            })
          );
          navigate("/menu");
          setter(false);
          setters([]);
        },
        (err) => {
          dispatch(
            notificationActions.showError({
              message: "Ha ocurrido un error al editar el usuario",
            })
          );
        }
      );
      navigate("/menu");
      setter(false);
      setters([]);
    }
  };

  function parseNumber(str) {
    if (typeof str !== "undefined") return str.replace(/[^0-9]/g, "");
    return "";
  }

  const roleDisabled = (itemName) => {
    const isUserChecked = checked.includes("Usuario");

    if (isUserChecked) {
      if (checked.length > 1) {
        return !checked.includes(itemName);
      }
      return false;
    }

    if (itemName !== "Usuario")
      return checked.length > 0 && !checked.includes(itemName);
  };


  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Typography variant="h5" align="center" gutterBottom>
        User
      </Typography>
      <Divider style={{ marginBottom: "1em" }} />
      <input {...register("id")} defaultValue={row?.id} type="hidden" />
      <Grid
        container
        direction="row"
        justifyContent="center"
        alignItems="stretch"
        spacing={1}
      >
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={row?.dynamicsId}
            name="dynamicsId"
            control={control}
            schema={fields.dynamicsId}
            formErrors={errors.dynamicsId}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={row?.userName}
            name="userName"
            control={control}
            schema={fields.userName}
            formErrors={errors.userName}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={row?.fullName}
            name="fullName"
            control={control}
            schema={fields.fullName}
            formErrors={errors.fullName}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={row?.email}
            name="email"
            control={control}
            schema={fields.email}
            formErrors={errors.email}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={parseNumber(customerData?.phone1)}
            name="phoneNumber"
            control={control}
            schema={fields.phoneNumber}
            formErrors={errors.phoneNumber}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={customerData?.address1}
            name="address"
            control={control}
            schema={fields.address}
            formErrors={errors.address}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={customerData?.city}
            name="city"
            control={control}
            schema={fields.city}
            formErrors={errors.city}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={customerData?.state}
            name="state"
            control={control}
            schema={fields.state}
            formErrors={errors.state}
          />
        </Grid>
        <Grid item xs={5}>
          <FormTextField
            style={{ width: "100%" }}
            size="small"
            value={customerData?.country}
            name="country"
            control={control}
            schema={fields.country}
            formErrors={errors.country}
          />
        </Grid>
        <Grid item xs={5} >
          <FormControlLabel
            label="Activo"
            control={
              <Checkbox checked={active} onChange={(e) => setActive(e.target.checked)} />
            }
          />
        </Grid>
        <Grid item xs={10}>
          <List dense>
            <Typography variant="h6">Role</Typography>
            {roles.map((item) => {
              return (
                <ListItemButton
                  key={item.id}
                  divider
                  dense
                  onClick={handleToggle(item.name)}
                  disabled={roleDisabled(item.name)}
                >
                  <ListItemText id={item.name} primary={`${item.name}`} />
                  <Checkbox
                    size="small"
                    color="primary"
                    onChange={handleToggle(item.name)}
                    checked={checked.indexOf(item.name) !== -1 || item.name === "Usuario"}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Grid>
        {row.id === undefined ? (
          <>
            <Grid item xs={10}>
              <Typography variant="h6" align="center" gutterBottom>
                Password
              </Typography>
              <Divider style={{ marginBottom: "1em" }} />
            </Grid>
            <Grid item xs={10} display="flex" justifyContent="center">
              <FormControlLabel
                label="Crear sin contraseña"
                control={
                  <Checkbox {...register("disabledPassword")} checked={disabledPassword} onChange={(e) => setDisabledPassword(e.target.checked)} />
                }
              />
            </Grid>
            <Grid item xs={4}>
              <FormTextField
                style={{ width: "100%" }}
                size="small"
                name="password"
                value={row.password}
                type="password"
                control={control}
                schema={fields.password}
                formErrors={errors.password}
                disabled={disabledPassword}
              />
            </Grid>
            <Grid item xs={4}>
              <FormTextField
                style={{ width: "100%" }}
                size="small"
                name="passwordConfirm"
                value={row.passwordConfirm}
                type="password"
                control={control}
                schema={fields.passwordConfirm}
                formErrors={errors.passwordConfirm}
                disabled={disabledPassword}
              />
            </Grid>
            <Typography variant="caption" align="center">
              <br />
              Utiliza 6 caracteres como mínimo, incluyendo una letra mayúscula,
              minúscula y un carácter no alfanumérico.
            </Typography>
          </>
        ) : null}
        <Grid item xs={6} style={{ paddingBottom: 2 }}>
          <Button fullWidth type="submit" sx={styles.button}>
            Confirmar
          </Button>
        </Grid>
      </Grid>
    </form>
  );
}
